import { normalizeSearchText } from '../../../utils/patientSearch';

export const ASSESSMENT_V2_COMPOSITE_REPORT_KEY = 'COMPOSITE_REPORT';
export const ASSESSMENT_V2_REPORT_KEY = 'ASSESSMENT_REPORT';

/** Catalog workflows open their own longitudinal flow. They are intentionally
 * absent from the clinical test keys consumed by sessions/results and runners. */
export const ASSESSMENT_V2_WORKFLOW_KEYS = ['PEAK_FLOW_DIARY'] as const;
export type AssessmentV2WorkflowKey = (typeof ASSESSMENT_V2_WORKFLOW_KEYS)[number];
export interface AssessmentV2WorkflowDefinition {
  label: string;
  description: string;
  searchTerms?: readonly string[];
}
export const ASSESSMENT_V2_WORKFLOWS: Readonly<Record<AssessmentV2WorkflowKey, AssessmentV2WorkflowDefinition>> = {
  PEAK_FLOW_DIARY: {
    label: 'Diário de peak flow',
    description: 'Acompanhamento seriado com um único link para as aferições do paciente.',
    searchTerms: ['diário', 'diario', 'peakflow', 'peak flow', 'acompanhamento', 'monitoramento', 'seriado', 'longitudinal', 'link único'],
  },
};

export const ASSESSMENT_V2_LABELS: Record<string, string> = {
  [ASSESSMENT_V2_COMPOSITE_REPORT_KEY]: 'Relatório de avaliações — modelo anterior',
  [ASSESSMENT_V2_REPORT_KEY]: 'Relatório de avaliações',
  PEAK_FLOW_DIARY: ASSESSMENT_V2_WORKFLOWS.PEAK_FLOW_DIARY.label,
  INITIAL_ASSESSMENT: 'Avaliação fisioterapêutica integrada',
  TC6: 'Teste de Caminhada de 6 minutos (TC6)',
  ISWT: 'Incremental Shuttle Walk Test (ISWT)',
  ESWT: 'Endurance Shuttle Walk Test (ESWT)',
  '6MST': 'Teste do Degrau de 6 minutos (6MST)',
  CHESTER: 'Chester Step Test',
  '5xSTS': 'Sentar-Levantar 5x',
  STS_30S: 'Sentar-Levantar 30s',
  STS_1MIN: 'Sentar-Levantar 1min',
  HANDGRIP: 'Força de preensão palmar (Handgrip)',
  PERIPHERAL_MUSCLE_STRENGTH: 'Força muscular periférica',
  ONE_RM: '1 repetição máxima (1RM)',
  PIMAX: 'PImax',
  PEMAX: 'PEmax',
  S_INDEX: 'S-Index',
  RESP_ENDURANCE: 'Endurance respiratório',
  PEAK_FLOW: 'Pico de fluxo expiratório (Peak Flow)',
  PEAK_COUGH_FLOW: 'Pico de fluxo de tosse (PFT/PCF)',
  CV_POSTURAL: 'Capacidade vital sentada e supina',
  SNIP: 'Pressão inspiratória nasal (SNIP)',
  TUG: 'Timed Up and Go (TUG)',
  '4MGS': 'Teste de velocidade da marcha de 4 metros (4MGS)',
  CAT: 'Questionário CAT',
  ACT: 'Questionário ACT',
  mMRC: 'Escala mMRC',
  'PFSDQ-M': 'PFSDQ-M',
  HADS: 'HADS',
  PSQI: 'Pittsburgh Sleep Quality Index (PSQI)',
  AQLQ: 'AQLQ',
  SGRQ: 'Saint George (SGRQ)',
  LCADL: 'LCADL',
  MLHFQ: 'MLHFQ',
  SAQ: 'SAQ',
};

export const ASSESSMENT_V2_ENABLED_TESTS = [
  'INITIAL_ASSESSMENT',
  'TC6',
  'ISWT',
  'ESWT',
  '6MST',
  'CHESTER',
  '5xSTS',
  'STS_30S',
  'STS_1MIN',
  'HANDGRIP',
  'PERIPHERAL_MUSCLE_STRENGTH',
  'ONE_RM',
  'PIMAX',
  'PEMAX',
  'S_INDEX',
  'RESP_ENDURANCE',
  'PEAK_FLOW',
  'PEAK_COUGH_FLOW',
  'CV_POSTURAL',
  'SNIP',
  'TUG',
  '4MGS',
  'CAT',
  'ACT',
  'mMRC',
  'PFSDQ-M',
  'HADS',
  'PSQI',
  'AQLQ',
  'SGRQ',
  'LCADL',
  'MLHFQ',
  'SAQ',
] as const;
export type AssessmentV2TestKey = (typeof ASSESSMENT_V2_ENABLED_TESTS)[number];

/** Instrumentos disponíveis para novas aplicações. SAQ permanece apenas para leitura histórica. */
export const ASSESSMENT_V2_CREATABLE_TESTS = ASSESSMENT_V2_ENABLED_TESTS.filter(
  (testKey) => testKey !== 'SAQ',
) as readonly AssessmentV2TestKey[];

export const ASSESSMENT_V2_QUESTIONNAIRE_TESTS = [
  'CAT',
  'ACT',
  'mMRC',
  'PFSDQ-M',
  'HADS',
  'PSQI',
  'AQLQ',
  'SGRQ',
  'LCADL',
  'MLHFQ',
  'SAQ',
] as const satisfies readonly AssessmentV2TestKey[];

export const isAssessmentV2QuestionnaireTest = (testKey: string): testKey is (typeof ASSESSMENT_V2_QUESTIONNAIRE_TESTS)[number] =>
  ASSESSMENT_V2_QUESTIONNAIRE_TESTS.includes(testKey as (typeof ASSESSMENT_V2_QUESTIONNAIRE_TESTS)[number]);

export type AssessmentV2CatalogSection = {
  id: string;
  title: string;
  description: string;
  testKeys: AssessmentV2TestKey[];
  workflowKeys?: AssessmentV2WorkflowKey[];
};

export const countAssessmentV2CatalogSection = (section: AssessmentV2CatalogSection): number =>
  section.testKeys.length + (section.workflowKeys?.length ?? 0);

export type AssessmentV2CatalogGroup = {
  id: string;
  title: string;
  description: string;
  sections: AssessmentV2CatalogSection[];
};

const ASSESSMENT_V2_BASE_GROUPS: AssessmentV2CatalogGroup[] = [
  {
    id: 'clinical-forms',
    title: 'Fichas clínicas',
    description: 'Registros estruturados para organizar o contexto e a síntese da avaliação.',
    sections: [
      {
        id: 'initial-clinical-assessment',
        title: 'Avaliação geral',
        description: 'Anamnese, exame, testes, metas e plano nos momentos inicial, reavaliação e final.',
        testKeys: ['INITIAL_ASSESSMENT'],
      },
    ],
  },
  {
    id: 'questionnaires',
    title: 'Questionários',
    description: 'Instrumentos clínicos autorreferidos para sintomas, controle, funcionalidade e qualidade de vida.',
    sections: [
      {
        id: 'symptoms-control',
        title: 'Sintomas e controle',
        description: 'Controle clínico e percepção de sintomas respiratórios.',
        testKeys: ['CAT', 'ACT', 'mMRC'],
      },
      {
        id: 'functional-status',
        title: 'Estado funcional e dispneia',
        description: 'Impacto dos sintomas nas atividades de vida diária.',
        testKeys: ['PFSDQ-M', 'LCADL'],
      },
      {
        id: 'quality-of-life',
        title: 'Qualidade de vida',
        description: 'Questionários específicos por condição clínica.',
        testKeys: ['AQLQ', 'SGRQ', 'MLHFQ', 'SAQ'],
      },
      {
        id: 'mental-health',
        title: 'Humor e saúde mental',
        description: 'Rastreamento emocional associado ao quadro clínico.',
        testKeys: ['HADS'],
      },
      {
        id: 'sleep-recovery',
        title: 'Sono e recuperação',
        description: 'Qualidade subjetiva do sono, arquitetura percebida e impacto diurno.',
        testKeys: ['PSQI'],
      },
    ],
  },
  {
    id: 'functional-tests',
    title: 'Testes funcionais',
    description: 'Avaliações de mobilidade, marcha e transferências funcionais.',
    sections: [
      {
        id: 'mobility-balance',
        title: 'Mobilidade e marcha',
        description: 'Mudança postural, marcha curta e risco funcional.',
        testKeys: ['TUG', '4MGS'],
      },
      {
        id: 'sit-to-stand',
        title: 'Sentar-levantar',
        description: 'Tempo, potência funcional e resistência em transferência repetida.',
        testKeys: ['5xSTS', 'STS_30S', 'STS_1MIN'],
      },
    ],
  },
  {
    id: 'muscle-respiratory-measures',
    title: 'Medidas musculares e respiratórias',
    description: 'Mensurações objetivas de força muscular, pressões respiratórias e fluxo expiratório.',
    sections: [
      {
        id: 'strength',
        title: 'Força',
        description: 'Mensuração objetiva de força muscular periférica.',
        testKeys: ['HANDGRIP', 'PERIPHERAL_MUSCLE_STRENGTH', 'ONE_RM'],
      },
      {
        id: 'respiratory-muscle',
        title: 'Força muscular respiratória',
        description: 'Pressões inspiratórias e expiratórias máximas, além de endurance respiratório com suporte a CSV do BreatheLink quando aplicável.',
        testKeys: ['PIMAX', 'PEMAX', 'SNIP', 'CV_POSTURAL', 'S_INDEX', 'RESP_ENDURANCE'],
      },
      {
        id: 'airflow-screening',
        title: 'Fluxo expiratório',
        description: 'Fluxo expiratório, pico de tosse e diário seriado com um único link.',
        testKeys: ['PEAK_FLOW', 'PEAK_COUGH_FLOW'],
        workflowKeys: ['PEAK_FLOW_DIARY'],
      },
    ],
  },
  {
    id: 'exercise-tests',
    title: 'Testes de exercício',
    description: 'Desempenho em esforço e capacidade funcional relacionada ao exercício.',
    sections: [
      {
        id: 'field-submaximal',
        title: 'Campo e submáximos',
        description: 'Testes aplicados fora do laboratório com foco funcional.',
        testKeys: ['TC6', 'ISWT', 'ESWT', '6MST', 'CHESTER'],
      },
    ],
  },
];

export const ASSESSMENT_V2_MODAL_GROUPS: AssessmentV2CatalogGroup[] = (() => {
  const enabledKeys = new Set<string>(ASSESSMENT_V2_CREATABLE_TESTS);

  const groups = ASSESSMENT_V2_BASE_GROUPS.map((group) => ({
    ...group,
    sections: group.sections
      .map((section) => ({
        ...section,
        testKeys: section.testKeys.filter((testKey) => enabledKeys.has(testKey)),
      }))
      .filter((section) => countAssessmentV2CatalogSection(section) > 0),
  })).filter((group) => group.sections.length > 0);

  const groupedKeys = new Set(
    groups.flatMap((group) => group.sections.flatMap((section) => section.testKeys)),
  );
  const uncategorized = ASSESSMENT_V2_CREATABLE_TESTS.filter((testKey) => !groupedKeys.has(testKey));

  if (!uncategorized.length) return groups;

  return [
    ...groups,
    {
      id: 'other',
      title: 'Outros',
      description: 'Testes disponíveis que ainda não receberam agrupamento clínico específico.',
      sections: [
        {
          id: 'other-tests',
          title: 'Disponíveis',
          description: 'Mantidos visíveis até serem classificados.',
          testKeys: uncategorized,
        },
      ],
    },
  ];
})();

export const getAssessmentV2Label = (testKey: string): string =>
  ASSESSMENT_V2_LABELS[testKey] ?? testKey;

/** Search the complete catalog, preserving workflow-only matches. Selection
 * counts/runnable tests remain a separate concern of the clinical runner. */
export const filterAssessmentV2CatalogGroups = (search: string): AssessmentV2CatalogGroup[] => {
  const term = normalizeSearchText(search);
  if (!term) return ASSESSMENT_V2_MODAL_GROUPS;
  const matches = (...values: readonly (string | undefined)[]) => values.some(value => normalizeSearchText(value).includes(term));
  return ASSESSMENT_V2_MODAL_GROUPS.map(group => ({
    ...group,
    sections: group.sections.map(section => ({
      ...section,
      testKeys: section.testKeys.filter(key => matches(getAssessmentV2Label(key), key, group.title, section.title)),
      ...(section.workflowKeys ? { workflowKeys: section.workflowKeys.filter(key => {
        const definition = ASSESSMENT_V2_WORKFLOWS[key];
        return matches(key, definition.label, definition.description, ...(definition.searchTerms ?? []), group.title, section.title);
      }) } : {}),
    })).filter(section => countAssessmentV2CatalogSection(section) > 0),
  })).filter(group => group.sections.length > 0);
};

export const isAssessmentV2ReportKey = (testKey?: string | null): boolean =>
  testKey === ASSESSMENT_V2_REPORT_KEY || testKey === ASSESSMENT_V2_COMPOSITE_REPORT_KEY;

export const getAssessmentV2ResultLabel = (result: {
  testKey: string;
  testVersion?: number;
  summaryJson?: Record<string, unknown> | null;
}): string => {
  if (result.testKey === 'INITIAL_ASSESSMENT') {
    const moment = result.summaryJson?.assessmentMoment;
    return moment === 'final' ? 'Avaliação fisioterapêutica final / alta'
      : moment === 'reassessment' ? 'Reavaliação fisioterapêutica'
        : 'Avaliação fisioterapêutica inicial';
  }
  if (isAssessmentV2ReportKey(result.testKey)) {
    const title = result.summaryJson?.title;
    if (typeof title === 'string' && title.trim()) return title.trim();
  }

  if (result.testKey === 'SAQ') {
    return 'Questionário de angina adaptado — versão legada';
  }

  if (result.testKey === 'LCADL' && (result.testVersion ?? 1) < 2) {
    return 'LCADL adaptada de 12 itens — versão legada';
  }

  if (result.testKey === 'HADS' && (result.testVersion ?? 1) < 2) {
    return 'HADS — pontuação legada';
  }

  return getAssessmentV2Label(result.testKey);
};

export const isAssessmentV2LegacyReadOnlyResult = (result: {
  testKey: string;
  testVersion?: number;
}): boolean =>
  result.testKey === 'SAQ'
  || (result.testKey === 'LCADL' && (result.testVersion ?? 1) < 2)
  || (result.testKey === 'HADS' && (result.testVersion ?? 1) < 2);
